<script setup>
import {arrow, computePosition, flip, offset, shift} from "@floating-ui/vue";
import {ref} from "vue";

const props = defineProps({
  placement: {
    type: String,
    default: "bottom",
  },
  delay: {
    type: String,
    default: '0',
  },
  strategy: {
    type: String,
    default: "absolute",
  },
});

const referenceRef = ref();
const floatingRef = ref();
const arrowRef = ref();
const isHidden = ref(true);
let timeout = ref(null);
let hideTimeout = ref(null);

async function calculatePosition() {
  const {x, y, middlewareData, placement} = await computePosition(
    referenceRef.value,
    floatingRef.value,
    {
        strategy: props.strategy,
        placement: props.placement,
        middleware: [
            offset(8),
            flip(),
            shift({padding: 5}),
            arrow({element: arrowRef.value}),
        ],
    }
  );

  Object.assign(floatingRef.value.style, {
    left: `${x}px`,
    top: `${y}px`,
  });

  const {x: arrowX, y: arrowY} = middlewareData.arrow;

  const opposedSide = {
    left: "right",
    right: "left",
    bottom: "top",
    top: "bottom",
  }[placement.split("-")[0]];

  Object.assign(arrowRef.value.style, {
    left: arrowX ? `${arrowX}px` : "",
    top: arrowY ? `${arrowY}px` : "",
    bottom: "",
    right: "",
    [opposedSide]: "-4px",
  });
}

function hide() {
    if (timeout) clearTimeout(timeout);
    if (hideTimeout) clearTimeout(hideTimeout);
    
    hideTimeout = setTimeout(() => {
        isHidden.value = true;
    }, 100); // Small delay to prevent flickering
}

function show() {
    if (hideTimeout) clearTimeout(hideTimeout);
    timeout = setTimeout(() => {
        isHidden.value = false;
        calculatePosition();
    }, props.delay)
}
</script>

<template>
  <div class="inline-block">
    <div
      ref="referenceRef"
      class="inline-block"
      @blur="hide"
      @focus="show"
      @mouseenter="show"
      @mouseleave="hide"
    >
        <slot name="reference"></slot>
    </div>
    <div
      ref="floatingRef"
      :class="[
        'absolute top-0 left-0 z-50 px-3 py-2 text-left  text-xsfont-medium text-white bg-gray-900 rounded-lg shadow-sm dark:bg-gray-500 normal-case tracking-normal',
        isHidden && 'hidden',
      ]"
      @mouseenter="show"
      @mouseleave="hide"
    >
        <slot name="floating"></slot>
      <div
        ref="arrowRef"
        class="absolute bg-gray-900 dark:bg-gray-500 h-[8px] w-[8px] rotate-45"
      ></div>
    </div>
  </div>
</template>